import React from 'react';
import { Box, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import {
  ESCALATION_CATALOG,
  instructionOptions,
  operatingHoursDescriptions,
  operatingHoursOptions,
  type EscalationInstruction,
  type OperatingHours,
} from 'skipify-types';

import type { AgenticEscalationSettings } from '../../../../store/modules/merchant/types';
import { radioSx } from '../../ellaFormStyles';
import { useOnboardingDraft } from '../OnboardingDraftContext';

const OPERATING_HOURS_LABELS: Record<OperatingHours, string> = {
  '24/7': '24/7',
  'business-hours': 'Business hours',
  'extended-retail': 'Extended retail',
  'weekends-only': 'Weekends only',
  'by-appointment': 'By appointment',
};

const isOperatingHours = (value: unknown): value is OperatingHours =>
  typeof value === 'string' && (operatingHoursOptions as readonly string[]).includes(value);

const isEscalationInstruction = (value: unknown): value is EscalationInstruction =>
  typeof value === 'string' && (instructionOptions as readonly string[]).includes(value);

const contactHintForInstruction = (instruction: EscalationInstruction): string => {
  if (instruction.includes('call our support phone')) {
    return 'Phone number is recommended for this instruction.';
  }
  if (instruction.includes('email our support') || instruction.includes('follow up by email')) {
    return 'Email is recommended for this instruction.';
  }
  if (instruction.includes('Contact Us link')) {
    return 'Contact URL is recommended for this instruction.';
  }
  return 'Contact details are optional and help the shopping agent hand shoppers off correctly.';
};

type Props = {
  embedded?: boolean;
};

export const OnboardingEscalationFields = ({ embedded = false }: Props): JSX.Element => {
  const { draft, updateDraft } = useOnboardingDraft();
  const escalation = draft.escalation;

  const thresholdInput = String(escalation.threshold);
  const parsedThreshold = Number(thresholdInput);
  const isThresholdValid =
    Number.isInteger(parsedThreshold) &&
    parsedThreshold >= ESCALATION_CATALOG.escalationThreshold.min &&
    parsedThreshold <= ESCALATION_CATALOG.escalationThreshold.max;

  const updateEscalation = (patch: Partial<AgenticEscalationSettings>) => {
    updateDraft((prev) => ({
      ...prev,
      escalation: { ...prev.escalation, ...patch },
    }));
  };

  return (
    <Box
      component="section"
      data-testid="ella-onboarding-escalation-section"
      sx={{ mb: embedded ? 0 : 4 }}
    >
      {!embedded ? (
        <>
          <Typography component="h3" variant="small" fontWeight="bold" sx={{ mb: 0.5 }}>
            Escalation
          </Typography>
          <Typography variant="small" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
            Configure when and how the shopping agent hands shoppers off to human support.
          </Typography>
        </>
      ) : null}

      <Box sx={{ mb: 3 }}>
        <Typography variant="small" fontWeight="bold" sx={{ mb: 0.5 }}>
          Escalation threshold
        </Typography>
        <Typography variant="small" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
          Order value in merchant currency above which conversations escalate (
          {ESCALATION_CATALOG.escalationThreshold.min.toLocaleString()}–
          {ESCALATION_CATALOG.escalationThreshold.max.toLocaleString()}).
        </Typography>
        <TextField
          fullWidth
          type="number"
          value={thresholdInput}
          onChange={(event) => {
            const next = Number(event.target.value);
            if (!Number.isNaN(next)) {
              updateEscalation({ threshold: next });
            }
          }}
          error={!isThresholdValid}
          helperText={
            !isThresholdValid
              ? `Enter a whole number between ${ESCALATION_CATALOG.escalationThreshold.min} and ${ESCALATION_CATALOG.escalationThreshold.max}.`
              : undefined
          }
          inputProps={{
            'data-testid': 'ella-onboarding-escalation-threshold',
            min: ESCALATION_CATALOG.escalationThreshold.min,
            max: ESCALATION_CATALOG.escalationThreshold.max,
            step: 1,
          }}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="small" fontWeight="bold" sx={{ mb: 0.5 }}>
          Operating hours
        </Typography>
        <Typography variant="small" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
          When human support is available for escalations.
        </Typography>
        <RadioGroup
          value={escalation.operatingHours}
          onChange={(_event, value) => {
            if (isOperatingHours(value)) {
              updateEscalation({ operatingHours: value });
            }
          }}
          sx={{ gap: 2 }}
        >
          {operatingHoursOptions.map((option) => (
            <Box
              key={option}
              component="label"
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, cursor: 'pointer' }}
            >
              <Radio
                size="small"
                value={option}
                checked={escalation.operatingHours === option}
                sx={{ ...radioSx, mt: '2px' }}
              />
              <Box>
                <Typography variant="medium" fontWeight="semibold">
                  {OPERATING_HOURS_LABELS[option]}
                </Typography>
                <Typography variant="small" color="text.secondary">
                  {operatingHoursDescriptions[option]}
                </Typography>
              </Box>
            </Box>
          ))}
        </RadioGroup>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="small" fontWeight="bold" sx={{ mb: 0.5 }}>
          Instruction
        </Typography>
        <Typography variant="small" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
          What the shopping agent should tell the shopper when escalating.
        </Typography>
        <RadioGroup
          value={escalation.instruction}
          onChange={(_event, value) => {
            if (isEscalationInstruction(value)) {
              updateEscalation({ instruction: value });
            }
          }}
          sx={{ gap: 1.5 }}
        >
          {instructionOptions.map((option) => (
            <Box
              key={option}
              component="label"
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, cursor: 'pointer' }}
            >
              <Radio
                size="small"
                value={option}
                checked={escalation.instruction === option}
                sx={{ ...radioSx, mt: '2px' }}
              />
              <Typography variant="medium" fontWeight="medium">
                {option}
              </Typography>
            </Box>
          ))}
        </RadioGroup>
      </Box>

      <Box>
        <Typography variant="small" fontWeight="bold" sx={{ mb: 0.5 }}>
          Contact details
        </Typography>
        <Typography variant="small" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
          Optional. {contactHintForInstruction(escalation.instruction)}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Contact phone number (optional)"
            placeholder="+1 (555) 555-5555"
            value={escalation.contactPhoneNumber ?? ''}
            onChange={(event) => updateEscalation({ contactPhoneNumber: event.target.value })}
            inputProps={{ 'data-testid': 'ella-onboarding-escalation-phone' }}
          />
          <TextField
            fullWidth
            label="Contact email (optional)"
            placeholder="help@example.com"
            value={escalation.contactEmail ?? ''}
            onChange={(event) => updateEscalation({ contactEmail: event.target.value })}
            inputProps={{ 'data-testid': 'ella-onboarding-escalation-email' }}
          />
          <TextField
            fullWidth
            label="Contact URL (optional)"
            placeholder="https://example.com/contact"
            value={escalation.contactUrl ?? ''}
            onChange={(event) => updateEscalation({ contactUrl: event.target.value })}
            inputProps={{ 'data-testid': 'ella-onboarding-escalation-url' }}
          />
        </Box>
      </Box>
    </Box>
  );
};
